import axios, { type AxiosResponse } from "axios";
import axiosInstance from "../api/axiosInstance";

/** Patient intake: a per-patient link to a hosted questionnaire that becomes a note. */

export type IntakeStatus = "pending" | "submitted" | "expired" | "revoked";

export type TIntakeRequest = {
  id: number;
  client_uuid: string;
  form_key: string;
  form_version: number;
  status: IntakeStatus;
  created_at: string;
  expires_at: string;
  submitted_at: string | null;
  revoked_at: string | null;
  audio_file_id: number | null;
  /** Only on the create response: the link is shown once and never stored. */
  url?: string;
};

export type IntakeOption = { key: string; label: string; exclusive?: boolean };

export type IntakeField = {
  key: string;
  label: string;
  type: "text" | "textarea" | "number" | "yes_no" | "radio" | "checkbox" | "family_history";
  required?: boolean;
  hint?: string;
  min?: number;
  max?: number;
  options?: IntakeOption[];
  relatives?: IntakeOption[];
};

export type IntakeSection = { key: string; title: string; fields: IntakeField[] };
export type IntakePage = { key: string; title: string; sections: IntakeSection[] };

export type IntakeForm = { key: string; version: number; title: string; pages: IntakePage[] };

export type IntakePublicForm = {
  status: IntakeStatus;
  patient: { name: string; date_of_birth: string | null; age: number | null; gender: string | null };
  practice_name: string | null;
  form: IntakeForm;
};

export type IntakeAnswers = Record<string, string | number | string[] | Record<string, string[]>>;

export const createIntakeRequest = (clientId: number): Promise<AxiosResponse<TIntakeRequest>> =>
  axiosInstance.post("/intake/requests/", { client_id: clientId });

export const listIntakeRequests = (clientId: number): Promise<AxiosResponse<TIntakeRequest[]>> =>
  axiosInstance.get("/intake/requests/", { params: { client_id: clientId } });

export const revokeIntakeRequest = (id: number): Promise<AxiosResponse<TIntakeRequest>> =>
  axiosInstance.post(`/intake/requests/${id}/revoke/`);

// The patient has no account: a bare client with no auth header or cookies, so a
// clinician's own session can never leak into the public form.
const publicClient = axios.create({ baseURL: process.env.REACT_APP_SERVER_URL });

export const getIntakePublicForm = (token: string): Promise<AxiosResponse<IntakePublicForm>> =>
  publicClient.get(`/intake/public/${encodeURIComponent(token)}/`);

export const submitIntakePublicForm = (
  token: string,
  answers: IntakeAnswers,
): Promise<AxiosResponse<{ status: string }>> =>
  publicClient.post(`/intake/public/${encodeURIComponent(token)}/submit/`, { answers });
